import { LucideIcon } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

interface MetricCardAction {
  label: string;
  onClick: () => void;
  variant?: "default" | "outline" | "ghost";
}

interface MetricCardBadge {
  text: string;
  variant?: "default" | "secondary" | "destructive" | "outline";
}

interface MetricCardProps {
  title: string;
  value: number | string;
  icon: LucideIcon;
  subtitle?: string;
  gradient: string;
  actions?: MetricCardAction[];
  badge?: MetricCardBadge;
  isLoading?: boolean;
}

export function MetricCard({
  title,
  value,
  icon: Icon,
  subtitle,
  gradient,
  actions,
  badge,
  isLoading = false,
}: MetricCardProps) {
  if (isLoading) {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent className="pb-3">
          <div className="flex items-start gap-4">
            <Skeleton className="h-12 w-12 rounded-lg" />
            <Skeleton className="h-10 w-24" />
          </div>
          {subtitle && <Skeleton className="h-4 w-full mt-2" />}
        </CardContent>
        {actions && actions.length > 0 && (
          <CardFooter className="pt-3 border-t">
            <div className="flex gap-2 w-full">
              {actions.map((_, idx) => (
                <Skeleton key={idx} className="h-9 flex-1" />
              ))}
            </div>
          </CardFooter>
        )}
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden transition-all duration-200 hover:shadow-lg hover:scale-[1.02] animate-fade-in">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
          {badge && (
            <Badge variant={badge.variant || "secondary"} className="ml-2">
              {badge.text}
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="pb-3">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-lg ${gradient}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="text-3xl font-bold tabular-nums">
              {value}
            </div>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
        </div>
      </CardContent>

      {actions && actions.length > 0 && (
        <CardFooter className="pt-3 border-t flex gap-2">
          {actions.map((action, idx) => (
            <Button
              key={idx}
              variant={action.variant || "ghost"}
              size="sm"
              onClick={action.onClick}
              className="flex-1"
            >
              {action.label}
            </Button>
          ))}
        </CardFooter>
      )}
    </Card>
  );
}
